import {
  type NshiftCreateShipmentRequest,
  type NshiftReceiver,
  type OldProductDTO,
  type OrderDTO
} from 'ecosystem'
import { calculateOrderTotalWeight } from 'shared-utils'
import type { OrderProvisionResult } from '../types'
import * as productsApi from '../../../productsEndpoint'
import { nshiftApi } from '../../../../nshift-api'
import { alertService } from '../../../../utils/alert-service'

export const generateNshiftShippingProviderResult = async (
  orderData: OrderDTO,
  contactInfo: Pick<NshiftReceiver, 'name' | 'email' | 'phone'>
): Promise<OrderProvisionResult['shippingProviderResult']> => {
  const nshiftDetails = orderData.shippingInfo?.additionalInfo?.nshiftDetails

  if (!nshiftDetails) {
    void alertService.send('Provision order | Get nshift data from order', {
      blocks: [
        {
          title: 'Missing data in',
          content: 'orderData.shippingInfo?.additionalInfo?.nshiftDetails'
        },
        {
          title: 'Order',
          content: orderData
        },
        {
          title: 'Error',
          content: 'No nshift shipping data'
        }
      ]
    })

    throw Error('No nshift shipping data')
  }

  // todo-nshift: check if needed
  if (nshiftDetails?.isPickupFromStore) {
    return {
      providerName: orderData.shippingProvider!,
      nshiftResponse: {
        isPickupFromStore: true
      }
    } satisfies OrderProvisionResult['shippingProviderResult']
  }

  // Check if nshift booking is created already
  if (nshiftDetails?.bookingDetails?.bookingOrderId) {
    return {
      providerName: orderData.shippingProvider!,
      nshiftResponse: nshiftDetails
    } satisfies OrderProvisionResult['shippingProviderResult']
  }

  if (!nshiftDetails?.widgetResult || !nshiftDetails?.recipientAddress) {
    void alertService.send('Provision order | Prepare data for Postnord booking creation', {
      blocks: [
        {
          title: 'Missing data in one of the properties',
          content: ['nshiftDetails?.widgetResult', 'nshiftDetails?.recipientAddress']
        },
        {
          title: 'Order',
          content: orderData
        },
        {
          title: 'Error',
          content: 'No nshift shipping details for booking creation'
        }
      ]
    })
    throw Error('No nshift shipping details for booking creation')
  }

  const fullProducts: OldProductDTO[] = await (async () => {
    const productIds = orderData.items?.map((item) => item.product.id) ?? []

    if (!productIds?.length) {
      return []
    }

    const productsResponse = await productsApi.listProductsFull(
      {
        productIds
      },
      {
        size: productIds.length
      }
    )

    return productsResponse.content
  })()

  if (fullProducts.length !== orderData.items?.length) {
    void alertService.send(
      'Provision order | Prepare data for Nshift booking creation | Get full products',
      {
        blocks: [
          {
            title: 'Full Products',
            content: fullProducts
          },
          {
            title: 'Order',
            content: orderData
          },
          {
            title: 'Error',
            content: 'Products are not found'
          }
        ]
      }
    )

    throw new Error('Products are not found')
  }

  const { totalWeight, extendedItems } = calculateOrderTotalWeight(orderData.items, fullProducts)

  const payload: NshiftCreateShipmentRequest = {
    orderId: orderData.orderNumber ?? '',
    sessionId: nshiftDetails.widgetResult.sessionId,
    optionId: nshiftDetails.widgetResult.optionId,
    pickupPointId: nshiftDetails.widgetResult.pickupPointId,
    timeSlotId: nshiftDetails.widgetResult.timeSlotId,
    addons: nshiftDetails.widgetResult.addons,
    fields: nshiftDetails.widgetResult.fields,
    receiver: {
      ...nshiftDetails.recipientAddress,
      name: contactInfo.name,
      email: contactInfo.email,
      phone: contactInfo.phone,
      mobile: contactInfo.phone
    },
    packages: [
      {
        weightKg: totalWeight,
        articles: extendedItems.map((item) => ({
          articleNo: item.product.articleNumber,
          quantity: item.quantity,
          weightKg: item.weight
        }))
      }
    ]
  }

  // Book nshift shipment
  const { data: nshiftResponseData, error: nshiftResponseError } =
    await nshiftApi.checkout.createPartialShipment(payload)

  if (nshiftResponseError || !nshiftResponseData) {
    void alertService.send('Provision order | Create Nshift booking', {
      blocks: [
        {
          title: 'Payload',
          content: payload
        },
        {
          title: 'Order',
          content: orderData
        },
        {
          title: 'Response data',
          content: nshiftResponseData
        },
        {
          title: 'Error',
          content: nshiftResponseError
        }
      ]
    })
    throw Error(nshiftResponseError || 'Nshift booking shipment error')
  }

  return {
    providerName: orderData.shippingProvider!,
    nshiftResponse: {
      bookingDetails: {
        bookingOrderId: nshiftResponseData.orderId,
        ...(nshiftResponseData.pickupPoint ? { pickupPoint: nshiftResponseData.pickupPoint } : {}),
        carrier: nshiftResponseData.carrier,
        carrierProduct: nshiftResponseData.carrierProduct
      },
      widgetResult: nshiftDetails.widgetResult,
      recipientAddress: nshiftDetails.recipientAddress
    }
  } satisfies OrderProvisionResult['shippingProviderResult']
}
